import { HttpClient } from "@angular/common/http";
import { Injectable, signal, WritableSignal } from "@angular/core";
import { Router } from "@angular/router";
import {
  catchError,
  finalize,
  firstValueFrom,
  map,
  Observable,
  tap,
} from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthToken } from "../../../interfaces/auth-token.interface";
import { SetPasswordData } from "../../../interfaces/set-password-data.interface";
import { User } from "../../../interfaces/user.interface";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  currentTokenResponse: WritableSignal<AuthToken | null> = signal(null);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  login(username: string, password: string): Observable<AuthToken> {
    return this.http
      .post<AuthToken>(environment.API_URL + "auth/login", {
        username,
        password,
      })
      .pipe(
        tap((tokenResponse) => {
          this.setNewTokenResponse(tokenResponse);
        }),
      );
  }

  logout(): Observable<void> {
    return this.http.post<void>(environment.API_URL + "auth/logout", {}).pipe(
      finalize(() => {
        this.currentTokenResponse.set(null);
        this.router.navigate(["auth/login"]);
      }),
    );
  }

  sendResetPasswordLink(email: string): Observable<void> {
    return this.http.post<void>(environment.API_URL + "auth/password/forgot", {
      email,
    });
  }

  resetPassword(newPassword: string, token: string) {
    return this.http.post<void>(environment.API_URL + "auth/password/reset", {
      token,
      newPassword,
    });
  }

  setNewTokenResponse(res: AuthToken): void {
    this.currentTokenResponse.set(res);
    localStorage.setItem("access_token", res?.access_token || "");
    localStorage.setItem("refresh_token", res?.refresh_token || "");
  }

  loadCurrentUser(): Promise<User> {
    const userObservable$ = this.fetchUserFromBackend();
    return firstValueFrom(userObservable$);
  }

  fetchUserFromBackend(): Observable<User> {
    return this.http.get<User>(environment.API_URL + "users/me", {
      params: { includeRoles: true },
    });
  }

  getAccessToken(): string | null {
    return localStorage.getItem("access_token");
  }

  getRefreshToken(): string | null {
    return localStorage.getItem("refresh_token");
  }

  refreshAccessToken(): Observable<AuthToken | void> {
    return this.http
      .post<AuthToken>(`${environment.API_URL}auth/token/refresh`, {
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        map((refreshTokenResponse) => {
          console.log("response from refreshToken call");
          console.log(refreshTokenResponse);
          this.setNewTokenResponse(refreshTokenResponse);
        }),
        catchError((error) => {
          this.logout();
          throw error;
        }),
      );
  }

  setPasswordForUser(setPasswordData: SetPasswordData): Observable<void> {
    return this.http.post<void>(
      `${environment.API_URL}auth/admin/password/set`,
      {
        ...setPasswordData,
      },
    );
  }

  sendResetPasswordEmail(email: string): Observable<void> {
    return this.http.post<void>(
      `${environment.API_URL}auth/admin/password/reset-email`,
      {
        email,
      },
    );
  }
}
